import { useRef, useCallback } from 'react';

interface SwipeGestureOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  minSwipeDistance?: number;
  maxSwipeTime?: number;
}

interface TouchPosition {
  x: number;
  y: number;
  time: number;
}

export function useSwipeGesture({
  onSwipeLeft,
  onSwipeRight,
  minSwipeDistance = 50,
  maxSwipeTime = 300,
}: SwipeGestureOptions) {
  const touchStart = useRef<TouchPosition | null>(null);
  const touchEnd = useRef<TouchPosition | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.targetTouches[0];
    touchStart.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };
    touchEnd.current = null;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.targetTouches[0];
    touchEnd.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStart.current || !touchEnd.current) return;

    const distanceX = touchEnd.current.x - touchStart.current.x;
    const distanceY = touchEnd.current.y - touchStart.current.y;
    const timeElapsed = touchEnd.current.time - touchStart.current.time;

    // Solo procesar si el tiempo de swipe es menor al máximo
    if (timeElapsed > maxSwipeTime) return;

    // Solo procesar si el movimiento horizontal es mayor que el vertical (swipe horizontal)
    if (Math.abs(distanceX) < Math.abs(distanceY)) return;

    // Solo procesar si la distancia mínima se alcanzó
    if (Math.abs(distanceX) < minSwipeDistance) return;

    if (distanceX > 0 && onSwipeRight) {
      onSwipeRight();
    } else if (distanceX < 0 && onSwipeLeft) {
      onSwipeLeft();
    }

    // Limpiar referencias
    touchStart.current = null;
    touchEnd.current = null;
  }, [onSwipeLeft, onSwipeRight, minSwipeDistance, maxSwipeTime]);

  return {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  };
}